import { useRef, useState } from 'react';
import clsx from 'clsx';
import {
  CheckCircle2,
  FileSpreadsheet,
  TriangleAlert,
  Upload,
  X,
} from 'lucide-react';
import { LESSON_HOURS_OPTIONS, stageLabel } from '@doit/shared';
import { Alert, Button, Card, Field, PageHeader } from '../../components/ui';
import { Checkbox, Select } from '../../components/form';
import { Badge } from '../../components/badges';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../api/client';
import {
  useFlows,
  useImportCommit,
  useImportPreview,
  useProjects,
} from '../../api/queries';
import type { EntityPlan, ImportOptions, ImportResult } from '../../api/types';

const MAX_MB = 5;

export function ImportPage() {
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: projects } = useProjects();
  const preview = useImportPreview();
  const commit = useImportCommit();

  const [fileName, setFileName] = useState('');
  const [csv, setCsv] = useState('');
  const [dragging, setDragging] = useState(false);

  const [projectId, setProjectId] = useState('');
  const [flowId, setFlowId] = useState('');
  const [stageCount, setStageCount] = useState('1');
  const [hoursPerStage, setHoursPerStage] = useState('0');
  const [lessonHours, setLessonHours] = useState('2');
  const [currentStage, setCurrentStage] = useState('1');
  const [createCatalog, setCreateCatalog] = useState(true);
  const [createLecturers, setCreateLecturers] = useState(true);
  const [updateStudents, setUpdateStudents] = useState(true);
  const [importHistory, setImportHistory] = useState(true);
  const [historyStage, setHistoryStage] = useState('1');

  // ნაკადების სია არჩეულ პროექტს მიჰყვება
  const { data: flows } = useFlows(projectId || undefined);

  const [result, setResult] = useState<ImportResult | null>(null);
  const [done, setDone] = useState(false);

  function options(): ImportOptions {
    return {
      csv,
      projectId: projectId || undefined,
      flowId: flowId || undefined,
      stageCount: Number(stageCount) || 1,
      hoursPerStage: Number(hoursPerStage) || 0,
      lessonHours: Number(lessonHours) || 2,
      currentStage: Number(currentStage) || 1,
      createCatalog,
      createLecturers,
      updateStudents,
      importHistory,
      historyStage: Number(historyStage) || 1,
    };
  }

  async function readFile(file: File) {
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`ფაილი ${MAX_MB} MB-ზე დიდია`);
      return;
    }
    const text = await file.text();
    setCsv(text);
    setFileName(file.name);
    setResult(null);
    setDone(false);
  }

  function clearFile() {
    setCsv('');
    setFileName('');
    setResult(null);
    setDone(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function runPreview() {
    try {
      setDone(false);
      setResult(await preview.mutateAsync(options()));
    } catch (err) {
      setResult(null);
      toast.error(err instanceof ApiError ? err.message : 'შემოწმება ვერ მოხერხდა');
    }
  }

  async function runCommit() {
    try {
      const r = await commit.mutateAsync(options());
      setResult(r);
      setDone(true);
      toast.success(`იმპორტი დასრულდა — ${r.importedRows} ჩანაწერი`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'იმპორტი ვერ მოხერხდა');
    }
  }

  const errorCount = result?.issues.filter((i) => i.level === 'error').length ?? 0;
  const warnCount = result?.issues.filter((i) => i.level === 'warning').length ?? 0;
  const busy = preview.isPending || commit.isPending;

  return (
    <>
      <PageHeader
        title="იმპორტი CSV-დან"
        subtitle="ჯგუფები, სტუდენტები და წინა ეტაპის შედეგები — ხელით შექმნის ნაცვლად"
      />

      <div className="grid gap-5 lg:grid-cols-3">
        {/* --- ფაილი --- */}
        <Card title="ფაილი" className="lg:col-span-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void readFile(f);
            }}
          />

          {csv ? (
            <div className="flex items-center gap-3 rounded-lg border border-line bg-canvas/60 px-4 py-3">
              <FileSpreadsheet className="size-5 shrink-0 text-brand-600" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-[14px] font-medium text-ink">{fileName}</p>
                <p className="tabular text-[12px] text-ink-mute">
                  {(csv.length / 1024).toFixed(1)} KB · {csv.split('\n').length - 1} ხაზი
                </p>
              </div>
              <button
                onClick={clearFile}
                aria-label="მოცილება"
                className="rounded-md p-1.5 text-ink-mute transition hover:bg-surface hover:text-danger"
              >
                <X className="size-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                const f = e.dataTransfer.files?.[0];
                if (f) void readFile(f);
              }}
              className={clsx(
                'flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 transition',
                dragging
                  ? 'border-brand-600 bg-brand-50'
                  : 'border-line-strong hover:border-brand-600 hover:bg-canvas/60',
              )}
            >
              <Upload className="size-7 text-ink-mute" />
              <span className="text-[14px] font-medium text-ink">
                ჩააგდე CSV ან დააკლიკე ასარჩევად
              </span>
              <span className="text-[12px] text-ink-mute">
                სვეტები: Group_Name, Full name, course_name, direction, provider, day, hour,
                lecture, email, Step mail, phoneNumber
              </span>
            </button>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={() => void runPreview()} disabled={!csv || busy} loading={preview.isPending}>
              შემოწმება
            </Button>
            <Button
              variant="secondary"
              onClick={() => void runCommit()}
              disabled={!csv || !result || done || busy}
              loading={commit.isPending}
            >
              {errorCount > 0 ? `იმპორტი (${result?.importedRows ?? 0} ხაზი)` : 'იმპორტი'}
            </Button>
            {result && !done && errorCount > 0 && (
              <span className="self-center text-[12px] text-danger">
                {errorCount} ხაზი გამოტოვდება — შენიშვნები ქვემოთ
              </span>
            )}
          </div>
        </Card>

        {/* --- პარამეტრები --- */}
        <Card title="პარამეტრები">
          <div className="space-y-4">
            <Select
              label="პროექტი"
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value);
                setFlowId('');
              }}
              placeholder="CSV-ის სვეტიდან"
              options={(projects ?? []).map((p) => ({ value: p.id, label: p.name }))}
              hint={`არ აირჩიო — და პროექტი „provider" სვეტიდან წაიკითხება`}
            />

            <Select
              label="ნაკადი"
              value={flowId}
              onChange={(e) => setFlowId(e.target.value)}
              placeholder="ჯგუფის კოდიდან"
              options={(flows ?? []).map((f) => ({ value: f.id, label: f.name }))}
              hint="არ აირჩიო — და ნაკადი ჯგუფის კოდიდან იკითხება: GAMT4-20-GC → 4"
            />

            <div className="rounded-lg border border-line bg-canvas/50 p-3">
              <p className="mb-3 text-[12px] font-semibold uppercase tracking-wide text-ink-mute">
                ახალი საგნებისთვის
              </p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label="ეტაპები"
                    type="number"
                    min={1}
                    max={10}
                    value={stageCount}
                    onChange={(e) => setStageCount(e.target.value)}
                  />
                  <Field
                    label="საათი ეტაპზე"
                    type="number"
                    min={0}
                    value={hoursPerStage}
                    onChange={(e) => setHoursPerStage(e.target.value)}
                  />
                </div>
                <Select
                  label="ლექციის ხანგრძლივობა"
                  value={lessonHours}
                  onChange={(e) => setLessonHours(e.target.value)}
                  options={LESSON_HOURS_OPTIONS.map((h) => ({
                    value: String(h),
                    label: `${h} საათი`,
                  }))}
                />
                <Select
                  label="ჯგუფის მიმდინარე ეტაპი"
                  value={currentStage}
                  onChange={(e) => setCurrentStage(e.target.value)}
                  options={Array.from({ length: Number(stageCount) || 1 }, (_, i) => ({
                    value: String(i + 1),
                    label: stageLabel(i + 1),
                  }))}
                />
              </div>
            </div>

            <div className="space-y-2.5">
              <Checkbox
                label="ახალი პროექტი / მიმართულება / საგანი / ნაკადი შეიქმნას"
                checked={createCatalog}
                onChange={setCreateCatalog}
              />
              <Checkbox
                label="ახალი ლექტორი შეიქმნას"
                checked={createLecturers}
                onChange={setCreateLecturers}
              />
              <Checkbox
                label="არსებულ სტუდენტს ცარიელი ველები შეევსოს"
                checked={updateStudents}
                onChange={setUpdateStudents}
              />
              <Checkbox
                label="წინა ეტაპის შედეგები შემოვიდეს"
                checked={importHistory}
                onChange={setImportHistory}
              />
              {importHistory && (
                <Select
                  label="რომელ ეტაპზე"
                  value={historyStage}
                  onChange={(e) => setHistoryStage(e.target.value)}
                  options={Array.from({ length: Number(stageCount) || 1 }, (_, i) => ({
                    value: String(i + 1),
                    label: stageLabel(i + 1),
                  }))}
                />
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* --- შედეგი --- */}
      {result && (
        <div className="mt-5 space-y-5">
          {done ? (
            <Alert kind="success">
              იმპორტი დასრულდა — {result.importedRows} ჩანაწერი დამუშავდა
              {result.skippedRows > 0 && `, ${result.skippedRows} გამოტოვდა`}.
            </Alert>
          ) : (
            <Alert kind={errorCount > 0 ? 'error' : 'info'}>
              ეს მხოლოდ შემოწმებაა — ბაზაში ჯერ არაფერი ჩაწერილა.
              {errorCount > 0 && ` ${errorCount} ხაზი გამოტოვდება.`}
            </Alert>
          )}

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Metric label="ხაზი ფაილში" value={result.totalRows} />
            <Metric label="დამუშავდება" value={result.importedRows} tone="ok" />
            <Metric
              label="გამოტოვდება"
              value={result.skippedRows}
              tone={result.skippedRows > 0 ? 'warn' : undefined}
            />
            <Metric
              label="გაფრთხილება"
              value={warnCount}
              tone={warnCount > 0 ? 'warn' : undefined}
            />
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <Card title={done ? 'შეიქმნა' : 'რა შეიქმნება'}>
              <div className="space-y-3">
                <PlanRow label="პროექტი" plan={result.plan.projects} />
                <PlanRow label="მიმართულება" plan={result.plan.directions} />
                <PlanRow label="საგანი" plan={result.plan.courses} />
                <PlanRow label="ნაკადი" plan={result.plan.flows} />
                <PlanRow label="ლექტორი" plan={result.plan.lecturers} />
                <PlanRow label="ჯგუფი" plan={result.plan.groups} />

                <div className="border-t border-line pt-3 text-[13px]">
                  <CountRow
                    label="სტუდენტი"
                    parts={[
                      ['ახალი', result.plan.students.created],
                      ['ნაპოვნი', result.plan.students.matched],
                      ['შევსებული', result.plan.students.updated],
                    ]}
                  />
                  <CountRow
                    label="ჩარიცხვა"
                    parts={[
                      ['ახალი', result.plan.enrollments.created],
                      ['არსებული', result.plan.enrollments.existing],
                    ]}
                  />
                  <CountRow
                    label="ისტორია"
                    parts={[
                      ['ახალი', result.plan.history.created],
                      ['განახლდება', result.plan.history.updated],
                    ]}
                  />
                </div>
              </div>
            </Card>

            <Card title={`შენიშვნები (${result.issues.length})`}>
              {result.issues.length === 0 ? (
                <p className="py-6 text-center text-[13px] text-ok">
                  <CheckCircle2 className="mr-1 inline size-4" />
                  პრობლემა არ არის
                </p>
              ) : (
                <ul className="max-h-72 space-y-1.5 overflow-y-auto">
                  {result.issues.map((issue, i) => (
                    <li
                      key={i}
                      className={clsx(
                        'flex items-start gap-2 rounded-md px-2.5 py-1.5 text-[12px]',
                        issue.level === 'error'
                          ? 'bg-danger-soft text-danger'
                          : 'bg-warn-soft text-warn',
                      )}
                    >
                      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                      <span>
                        <strong className="tabular">ხაზი {issue.row}</strong> — {issue.message}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>

          {result.sample.length > 0 && (
            <Card title="პირველი ხაზები — გადაამოწმე">
              <div className="-mx-5 overflow-x-auto">
                <table className="w-full min-w-[46rem] text-[13px]">
                  <thead className="border-y border-line bg-canvas/70 text-left text-[11px] uppercase tracking-wide text-ink-mute">
                    <tr>
                      <th className="px-4 py-2.5 font-semibold">ხაზი</th>
                      <th className="px-4 py-2.5 font-semibold">სტუდენტი</th>
                      <th className="px-4 py-2.5 font-semibold">ჯგუფი</th>
                      <th className="px-4 py-2.5 font-semibold">საგანი</th>
                      <th className="px-4 py-2.5 font-semibold">ლექტორი</th>
                      <th className="px-4 py-2.5 font-semibold">ტელეფონი</th>
                      <th className="px-4 py-2.5 text-right font-semibold">წინა ეტაპი</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.sample.map((s) => (
                      <tr key={s.row} className="border-t border-line">
                        <td className="tabular px-4 py-2.5 text-ink-mute">{s.row}</td>
                        <td className="px-4 py-2.5 font-medium text-ink">{s.fullName}</td>
                        <td className="tabular px-4 py-2.5 text-ink-soft">{s.group}</td>
                        <td className="max-w-56 truncate px-4 py-2.5 text-ink-soft">{s.course}</td>
                        <td className="px-4 py-2.5 text-ink-soft">{s.lecturer || '—'}</td>
                        <td className="tabular px-4 py-2.5 text-ink-soft">{s.phone || '—'}</td>
                        <td className="tabular px-4 py-2.5 text-right text-ink-soft">
                          {s.attendancePct !== null ? `${s.attendancePct}%` : '—'}
                          {s.result && (
                            <span className="ml-1.5 text-[11px] text-ink-mute">{s.result}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}
    </>
  );
}

// ============================================================

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'ok' | 'warn';
}) {
  return (
    <div className="rounded-[10px] border border-line bg-surface px-4 py-3.5 shadow-xs">
      <p className="text-[12px] text-ink-mute">{label}</p>
      <p
        className={clsx(
          'tabular mt-1 text-2xl font-semibold',
          tone === 'ok' ? 'text-ok' : tone === 'warn' ? 'text-warn' : 'text-ink',
        )}
      >
        {value}
      </p>
    </div>
  );
}

function PlanRow({ label, plan }: { label: string; plan: EntityPlan }) {
  if (plan.created.length === 0 && plan.existing.length === 0) return null;

  return (
    <div className="text-[13px]">
      <div className="mb-1 flex items-baseline justify-between">
        <span className="font-medium text-ink">{label}</span>
        <span className="tabular text-[12px] text-ink-mute">
          {plan.created.length > 0 && <span className="text-ok">+{plan.created.length} ახალი</span>}
          {plan.created.length > 0 && plan.existing.length > 0 && ' · '}
          {plan.existing.length > 0 && `${plan.existing.length} არსებული`}
        </span>
      </div>
      {plan.created.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {plan.created.slice(0, 12).map((name) => (
            <Badge key={name} tone="ok">
              {name}
            </Badge>
          ))}
          {plan.created.length > 12 && <Badge>+{plan.created.length - 12}</Badge>}
        </div>
      )}
    </div>
  );
}

function CountRow({ label, parts }: { label: string; parts: Array<[string, number]> }) {
  return (
    <div className="flex items-baseline justify-between py-1">
      <span className="font-medium text-ink">{label}</span>
      <span className="tabular text-[12px] text-ink-soft">
        {parts
          .filter(([, n]) => n > 0)
          .map(([k, n]) => `${k} ${n}`)
          .join(' · ') || '—'}
      </span>
    </div>
  );
}
